import { useMedChronEditingLease } from '../../hooks/useMedChronEditingLease'
import { useEffect, useState } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { FilePdf, CheckCircle, Clock, AlertErrorCircle, InvalidFile } from '@/assets/icons'
import { useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import { DeletedFileBadge } from '@/matter-asset-common/files/components/DeletedFileBadge'
import { useDeletedFileIds } from '@/matter-asset-common/files/hooks/useDeletedFileIds'
import { selectIsUpdateActive, selectMedChronAssetFiles, selectMedChronAssetForMatter } from '../../store/selectors'
import { MedChronAssetFileStatus, MedChronAssetFileView } from '../../store/schemas'
import RemoveFileDialog from './RemoveFileDialog'
import { SourceFileOverflowMenu } from './SourceFileOverflowMenu'

// Figma renders Last Processed as two columns: a (relative) date + a 12-hour time.
function formatDate(ms: number): string {
  const d = new Date(ms)
  const now = new Date()
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) return 'Today'
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}
function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

/**
 * The per-file status sub-line under the filename. Figma (8716:98666) designs only the extracted
 * happy state: a green check-circle + green "N events extracted". Pending/processing/failed are not
 * mocked, so they reuse the existing in-app sub-line patterns (spinner / clock / red alert).
 */
function FileStatusLine({ file, isDeleted }: { file: MedChronAssetFileView; isDeleted: boolean }) {
  if (isDeleted) {
    return <DeletedFileBadge linkedEvents={file.events_extracted} />
  }

  const events = `${file.events_extracted} ${file.events_extracted === 1 ? 'event' : 'events'} extracted`

  switch (file.status) {
    case MedChronAssetFileStatus.INCLUDED:
    case MedChronAssetFileStatus.EXTRACTED:
      return (
        <div className="flex items-center gap-1 text-green-800">
          <CheckCircle className="w-3.5 h-3.5 shrink-0" aria-hidden />
          <p className="text-xs font-medium">{events}</p>
        </div>
      )
    case MedChronAssetFileStatus.PROCESSING:
      return (
        <div className="flex items-center gap-1 text-brand-neutral-600">
          <span className="w-3.5 h-3.5 shrink-0 flex items-center justify-center" aria-hidden>
            <CircularProgressContinuousSized size={12} thickness={4} />
          </span>
          <p className="text-xs font-medium italic">Processing…</p>
        </div>
      )
    case MedChronAssetFileStatus.PENDING:
      return (
        <div className="flex items-center gap-1 text-brand-neutral-600">
          <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden />
          <p className="text-xs font-medium">Pending</p>
        </div>
      )
    case MedChronAssetFileStatus.FAILED:
      return (
        <div className="flex items-center gap-1 text-red-800">
          <AlertErrorCircle className="w-3.5 h-3.5 shrink-0" aria-hidden />
          <p className="text-xs font-medium">{file.error_reason || 'Processing failed'}</p>
        </div>
      )
  }
}

/**
 * Source Files tab (Figma "Source Files" 8716:98647): the chronology's file roster as a list of
 * upload-card rows — PDF-icon gutter, filename, an extraction status sub-line, a two-column
 * Last Processed (date + time), and a kebab overflow menu whose single action removes the file.
 */
export default function SourceFilesTab() {
  const assetId = useMatterAssetStoreKey()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const matterId = activeMatterId ?? undefined
  const asset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId }))
  const files = useAppSelector((state: RootState) => selectMedChronAssetFiles(state, { assetId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const lease = useMedChronEditingLease()
  const isReadOnly = !lease.canWrite

  const [fileToRemove, setFileToRemove] = useState<MedChronAssetFileView | null>(null)
  const { deletedFileIds } = useDeletedFileIds(files?.map((file) => file.user_file_id) ?? [])

  useEffect(() => {
    if (isReadOnly) setFileToRemove(null)
  }, [isReadOnly])

  if (files === null) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <CircularProgressContinuousSized size={20} thickness={7} />
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto p-2 lg:p-4">
      {/* Header labels: summary (left) + Last Processed (right), matching Figma's progress header (8716:98657). */}
      <div className="flex items-center justify-between px-1 pb-3 text-sm font-medium tracking-[-0.15px] text-brand-neutral-800">
        {asset && (
          <span>
            {asset.total_events} {asset.total_events === 1 ? 'event' : 'events'} extracted from {files.length} {files.length === 1 ? 'document' : 'documents'}
          </span>
        )}
        {files.length > 0 && <span className="hidden w-[204px] shrink-0 sm:block">Last Processed</span>}
      </div>
      {/* Divider under the stats count, matching Figma's progress-header rule (8716:98657). */}
      <hr className="mb-3 h-px border-0 bg-brand-neutral-200" />

      {files.length === 0 ? (
        <div className="rounded-[5px] border border-brand-neutral-200 bg-white px-3 py-6 text-sm text-brand-neutral-600 text-center">No source files yet.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {files.map((file) => {
            const isDeleted = deletedFileIds.has(file.user_file_id)

            return (
              <div key={file.user_file_id} className="h-[60px] flex items-center gap-[10px] pr-3 rounded-[5px] border border-brand-neutral-200 bg-white">
                <div className="w-[60px] h-full flex items-center justify-center border-r border-brand-neutral-200 rounded-l-[5px]">
                  {isDeleted ? (
                    <InvalidFile className="w-6 h-6 text-true-gray-300" aria-hidden />
                  ) : (
                    <FilePdf className="w-6 h-6 text-brand-neutral-800" aria-hidden />
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1 items-start justify-center">
                  <p
                    className={`min-w-0 max-w-full text-[13px] font-medium truncate ${isDeleted ? 'text-true-gray-500' : 'text-brand-neutral-950'}`}
                    title={file.display_name}
                  >
                    {file.display_name}
                  </p>
                  <FileStatusLine file={file} isDeleted={isDeleted} />
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-[13px] font-normal tracking-[-0.15px] text-brand-neutral-800 shrink-0">
                  <span className="w-20">{file.last_processed_at ? formatDate(file.last_processed_at) : '—'}</span>
                  <span className="w-16">{file.last_processed_at ? formatTime(file.last_processed_at) : ''}</span>
                </div>
                <div className="shrink-0">
                  <SourceFileOverflowMenu onRemove={() => setFileToRemove(file)} disabled={isReadOnly || isUpdateActive} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {fileToRemove && assetId && matterId && (
        <RemoveFileDialog
          open={Boolean(fileToRemove)}
          assetId={assetId}
          matterId={matterId}
          userFileId={fileToRemove.user_file_id}
          displayName={fileToRemove.display_name}
          onClose={() => setFileToRemove(null)}
        />
      )}
    </div>
  )
}
